import { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import toast from 'react-hot-toast';

import {
  ProgramActivityDataFragment,
  useProgramAssessmentLazyQuery,
} from '../../../../generated/graphql';

import { useAuth } from '../../../../contexts/AuthContext';

import { defaultTransitionProps } from '../../../lib/animation';

import Spinner from '../../../svgs/Spinner';
import IllustrationMaze from '../../../svgs/IllustrationMaze';

import ToastAlert from '../../../components/ToastAlert';
import ActivityTable from '../../../components/ActivityTable';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import ProgramActivitySlideover from '../../../components/ProgramActivitySlideover';
import FormResponsesModal from '../../../components/Modals/FormResponsesModal';
import AddOrEditTextModal from '../../../components/Modals/AddOrEditTextModal';

import { usePatientProfileContext } from '..';

const Activities = () => {
  const {
    patient,
    getProgramActivities,
    refetchProgramActivities,
    programActivitiesData,
    programActivitiesError,
    programActivitiesLoading,
  } = usePatientProfileContext();

  const { authedProviderUser } = useAuth();

  const [unmounted, setUnmounted] = useState(false);

  const [selectedProgramActivity, setSelectedProgramActivity] =
    useState<ProgramActivityDataFragment>();
  const [isProgramActivitySlideoverOpen, setIsProgramActivitySlideoverOpen] =
    useState<boolean>(false);
  const [isFormResponsesModalOpen, setIsFormResponsesModalOpen] =
    useState(false);
  const [selectedProgramActivityForModal, setSelectedProgramActivityForModal] =
    useState<ProgramActivityDataFragment>();
  const [fullProgramAssessment, setFullProgramAssessment] = useState<any>();
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [selectedActivityText, setSelectedActivityText] =
    useState<ProgramActivityDataFragment['activityText']>();

  // Lazy query to fetch full program assessment data (including answers)
  const [getProgramAssessment] = useProgramAssessmentLazyQuery();

  useEffect(() => {
    getProgramActivities();
    setUnmounted(true);
  }, []);

  useEffect(() => {
    if (programActivitiesError) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to fetch activities."
          level="error"
        />
      ));
    }
  }, [programActivitiesError]);

  const programActivities = programActivitiesData?.programActivities;

  return (
    <>
      {programActivitiesLoading && <Spinner className="mx-auto mt-8" />}
      <Transition
        show={Boolean(
          programActivities &&
            !programActivitiesError &&
            !programActivitiesLoading &&
            unmounted,
        )}
        {...defaultTransitionProps}
        className="w-full pt-4"
      >
        {Boolean(programActivities?.length) && (
          <ActivityTable
            programActivities={programActivities}
            programActivitiesLoading={programActivitiesLoading}
            patientName={patient?.name}
            programId={patient?.programInstanceId}
            onClickProgramActivity={(programActivity) => {
              setSelectedProgramActivity(programActivity);
              setIsProgramActivitySlideoverOpen(true);
            }}
            onExpandResponses={async (programActivity) => {
              setSelectedProgramActivityForModal(programActivity);

              // Fetch full program assessment data including answers
              if (programActivity?.programAssessment?.id) {
                try {
                  const { data } = await getProgramAssessment({
                    variables: {
                      programAssessmentId: programActivity.programAssessment.id,
                    },
                  });

                  if (data?.programAssessment) {
                    setFullProgramAssessment(data.programAssessment);
                  }
                } catch (error) {
                  console.error('Error fetching program assessment:', error);
                }
              }

              setIsFormResponsesModalOpen(true);
            }}
            onTextOpen={(activityText) => {
              setSelectedActivityText(activityText);
              setIsTextModalOpen(true);
            }}
          />
        )}
        {!Boolean(programActivities?.length) && (
          <EmptyProfileTab
            IconComponent={IllustrationMaze}
            iconClassName={'text-green-125'}
            titleText={`${patient?.firstName ?? ''}'s activities and resources`}
            subTitleText={`All the activities and resources you send to ${patient?.firstName} through a
              follow-up will show up here.`}
          />
        )}
      </Transition>

      <ProgramActivitySlideover
        isOpen={isProgramActivitySlideoverOpen}
        onClose={() => setIsProgramActivitySlideoverOpen(false)}
        programActivity={selectedProgramActivity}
        authedProviderUserId={authedProviderUser?.id}
        patientName={patient?.name}
        programId={patient?.programInstanceId}
        onVoided={async () => {
          setIsProgramActivitySlideoverOpen(false);
          await refetchProgramActivities();
        }}
      />

      <FormResponsesModal
        isOpen={isFormResponsesModalOpen}
        onClose={() => {
          setIsFormResponsesModalOpen(false);
          setFullProgramAssessment(undefined);
        }}
        programAssessment={
          fullProgramAssessment ||
          (selectedProgramActivityForModal?.programAssessment as any)
        }
        assessmentName={
          selectedProgramActivityForModal?.programAssessment?.assessment?.name
        }
        patientName={patient?.name}
      />

      <AddOrEditTextModal
        isModalOpen={isTextModalOpen}
        setClosed={() => setIsTextModalOpen(false)}
        selectedActivityText={selectedActivityText}
        viewOnly
      />
    </>
  );
};

export default Activities;
